// Polaroid — white-border photo card with caption + tilt.
// Ported from design-canon/project/src/components.jsx (the .polaroid CSS class
// is in canon.css; this is the React wrapper).
//
// Tilt-in animation: every Polaroid plays a one-shot `polaroid-in` keyframe on
// mount — drops from a steeper angle into the resting tilt-l / tilt-r pose.
// The keyframe lives in stumble-overrides.css (not in canon.css since the
// canon design doesn't pin a specific keyframe name for it).
//
// Reduced motion: skip the animation; the static rotate(-3deg | 3deg) from
// the canon tilt-l / tilt-r classes is the still pose.

import type { CSSProperties, ImgHTMLAttributes } from 'react'
import { seededRandom } from '../lib/media/seededRandom'

// React's <img> typing intentionally forbids unknown attributes. We pass
// data-* through here for testability (E9 polaroid-img filter-swap assertion)
// so widen the type with an index signature for `data-…` keys.
type ImgPassthrough = ImgHTMLAttributes<HTMLImageElement> & {
  [dataKey: `data-${string}`]: string | undefined
}

export function Polaroid({
  src,
  caption,
  tilt = 'l',
  alt,
  style,
  /** Optional seed for a stable in-animation delay (per-photo). */
  seed,
  /** Forwarded to the inner <img>. Used by SelfieSheet to attach
   *  data-testid + data-filtered for the E9 filter-swap assertion. */
  imgProps,
}: {
  src: string
  caption?: string
  tilt?: 'l' | 'r' | 'none'
  alt: string
  style?: CSSProperties
  seed?: string
  imgProps?: ImgPassthrough
}) {
  const cls = tilt === 'l' ? 'polaroid tilt-l' : tilt === 'r' ? 'polaroid tilt-r' : 'polaroid'
  const enterDelay = polaroidDelayMs(seed ?? src)
  return (
    <div className={`${cls} polaroid-in`} style={{ animationDelay: `${enterDelay}ms`, ...style }}>
      <div className="photo">
        {/* Spread before src/alt so they can't be silently overridden;
            biome a11y rule needs the alt attr literal on the element. */}
        <img {...imgProps} src={src} alt={alt} />
      </div>
      {caption ? <div className="cap">{caption}</div> : null}
    </div>
  )
}

export function PolaroidPlaceholder({
  caption,
  tilt = 'l',
  bg = 'var(--bg-deep)',
  seed,
}: {
  caption?: string
  tilt?: 'l' | 'r' | 'none'
  bg?: string
  seed?: string
}) {
  const cls = tilt === 'l' ? 'polaroid tilt-l' : tilt === 'r' ? 'polaroid tilt-r' : 'polaroid'
  const enterDelay = polaroidDelayMs(seed ?? caption ?? 'placeholder')
  return (
    <div className={`${cls} polaroid-in`} style={{ animationDelay: `${enterDelay}ms` }}>
      <div className="photo" style={{ background: bg }} />
      {caption ? <div className="cap">{caption}</div> : null}
    </div>
  )
}

// 0–180ms stable per-seed jitter so a row of Polaroids drops in cascade,
// not simultaneously. seededRandom is deterministic — same seed → same delay
// across re-renders, so no flash on React state updates.
function polaroidDelayMs(seed: string): number {
  const rng = seededRandom(seed)
  return Math.round(rng() * 180)
}
